import { Button, Card, CardContent, CardFooter, ErrorLabel, Input, Textarea } from '@ibsys/kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { PlusIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PredicateInput } from '@/features/PredicateInput';
import { cn } from '@/shared/lib';
import { Icon } from '@/shared/ui';
import { NEW_ID, SingleTermFormValues, createTermSchemaFactory } from './types';

type Props = {
  term: SingleTermFormValues;
  onRemove: () => void;
  onAdd?: () => void;
  onSave?: (values: SingleTermFormValues) => Promise<SingleTermFormValues> | SingleTermFormValues;
  onValidate?: (hasError: boolean) => void;
  isLast: boolean;
  /** Доступно ли редактирование (право на администрирование терминов) */
  canEdit: boolean;
  canAddNew: boolean;
};

export const TermItem: FC<Props> = (props) => {
  const { term, onRemove, onAdd, onSave, onValidate, isLast, canEdit, canAddNew } = props;

  const { t } = useTranslation('terms');
  const schema = useMemo(() => createTermSchemaFactory(t), [t]);
  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<SingleTermFormValues>({
    resolver: zodResolver(schema),
    defaultValues: term,
    mode: 'onChange',
    shouldFocusError: false,
  });

  useEffect(() => {
    if (term.id === NEW_ID) {
      setFocus('name');
    }
  }, []);

  useEffect(() => {
    reset(term, { keepDirty: false });
  }, [term, reset]);

  const hasError = !!errors.name || !!errors.description || !!errors.predicate;

  useEffect(() => {
    onValidate?.(hasError);
  }, [hasError, onValidate]);

  const termId = term.id;

  const allowRemove = termId !== NEW_ID || (termId === NEW_ID && !isLast);

  const hint = useMemo(() => {
    if (!isLast || !canEdit) {
      return '';
    }

    return t('shiftEnterAdd');
  }, [isLast, canEdit, t]);

  const handleAddClick = useCallback(() => {
    if (canAddNew) {
      onAdd?.();
    }
  }, [canAddNew, onAdd]);

  const handleBlur = handleSubmit(async (values) => {
    if (!isDirty || !onSave) {
      return;
    }

    const saved = await onSave(values);
    reset(saved, { keepDirty: false });
  });

  return (
    <Card className="flex flex-col @container/term-item">
      <CardContent className="flex flex-col gap-4 pt-6">
        <form
          className="grid grid-cols-1 @xl/term-item:grid-cols-2 gap-2 "
          onBlur={handleBlur}
          onSubmit={(e) => e.preventDefault()}
        >
          <Input
            control={control}
            name="name"
            className="min-h-full w-full col-span-full"
            placeholder={t('namePlaceholder')}
            required
            disabled={!canEdit}
          />
          <Textarea
            control={control}
            name="description"
            className="min-h-full row-start-2"
            style={{ resize: 'none' }}
            classNames={{ input: cn('min-h-full') }}
            placeholder={t('descriptionPlaceholder')}
            required
            error={' '}
            disabled={!canEdit}
          />
          {errors?.description && (
            <ErrorLabel className={'col-start-1 col-end-1 row-start-3'}>{errors?.description?.message}</ErrorLabel>
          )}
          <PredicateInput
            control={control}
            name="predicate"
            className="min-h-full"
            classNames={{ input: 'min-h-full' }}
            disabled={!canEdit}
          />
          {errors?.predicate && (
            <ErrorLabel className={'col-start-2 col-end-2 row-start-3'}>{errors?.predicate?.message}</ErrorLabel>
          )}
        </form>
      </CardContent>

      <CardFooter className="border-border border-t flex justify-between items-center">
        <div className="flex gap-2 items-center">
          {canEdit && allowRemove && (
            <Button
              tabIndex={-1}
              icon={<Icon type="trash" height={16} width={16} />}
              variant="ghost"
              className="size-7"
              onClick={onRemove}
              color="destructive"
            />
          )}

          {!!hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </div>
        {isLast && canEdit && (
          <Button
            variant="outline"
            onClick={handleAddClick}
            icon={<PlusIcon />}
            size="sm"
            disabled={!canAddNew}
            tabIndex={-1}
          >
            {t('addTerm')}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
